import { useEffect, useState, useRef } from "react";
import { Box, Grid, Paper, Typography, Checkbox, Divider } from "@mui/material";
import type { SvgIconComponent } from "@mui/icons-material";
import { ArrowForward, Bolt, Settings } from "@mui/icons-material";

import { QuoteModeEnum } from "../../../quotes/quote-create-stepper/enums/quote-create.enums";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks/redux";
import { setScopeOfWorkMode } from "../../../../redux/features/jobCreateSlice";
import { colors } from "../../../ui/colors";
import { DataF } from "@react-google-maps/api";
import { assets } from "../../../../assets";
import lottie from "lottie-web";
import { RiveByKey } from "../../../riveAnimation";
import { riveRegistry } from "../../../../assets/animations/riveRegistry/riveRegistry";

type ModeOption = {
  title: string;
  subtitle: string;
  mode: QuoteModeEnum;
  //imageUrl: string;
  //icon: SvgIconComponent;
  animationData: object;
  description: string;
  cta: string;
  color: { base: string; 50: string; 100: string; 200: string };
};

const modeOptions: ModeOption[] = [
  {
    title: "Basic",
    subtitle: "Fast and Easy",
    mode: QuoteModeEnum.BASIC,
    //icon: Bolt,
    //imageUrl: "https://flownect-media-dev.s3.us-west-1.amazonaws.com/images/createjob/basic.webp",
    animationData: assets.animation.createJobBasic,
    description: "Get a quote quickly with a simpler setup.",
    color: { base: "#FF7800", 50: "#FFF3E0", 100: "#FFE0B2", 200: "#FFCC80" },
    cta: "Start Basic",
  },
  {
    title: "Advanced",
    subtitle: "More Details, More Accuracy",
    mode: QuoteModeEnum.ADVANCED,
    //icon: Settings,
    //imageUrl: "https://flownect-media-dev.s3.us-west-1.amazonaws.com/images/createjob/advaced.webp",
    animationData: assets.animation.createJobAdvanced,
    description: "Customize your quote with areas, items, and special requests.",
    color: { base: "#1976D2", 50: "#BBDEFB", 100: "#BBDEFB", 200: "#90CAF9" },
    cta: "Start Advanced",
  },
];

function ModeCard({
  option,
  index,
  show,
  selected,
  onPick,
}: {
  option: ModeOption;
  index: number;
  show: boolean;
  selected: boolean;
  onPick: (m: QuoteModeEnum) => void;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animationRef = useRef<any>(null);
  // const Icon = option.icon;

  useEffect(() => {
    if (!containerRef.current) return;

    animationRef.current = lottie.loadAnimation({
      container: containerRef.current,
      renderer: "svg",
      loop: true,
      autoplay: true,
      animationData: option.animationData,
    });

    return () => animationRef.current?.destroy();
  }, [option.animationData]);

  /* useEffect(() => {
    if (!animationRef.current) return;

    if (selected) {
      animationRef.current.play();
    } else {
      animationRef.current.stop();
    }
  }, [selected]); */

  return (
    <Grid item xs={12} md={4} sx={{ display: "flex", marginTop: 2 }}>
      <Box
        onClick={() => onPick(option.mode)}
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: { xs: 520, md: 200 },
          maxHeight: { xs: 520, md: 380 },

          // aspectRatio: { xs: "auto", md: "4 / 3" },
          mx: "auto",
          borderRadius: "24px",
          overflow: "hidden",

          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.92)",
          // border: selected ? `1px solid ${option.color.base}` : `1px solid ${colors.gray[100]}`,

          cursor: "pointer",

          opacity: show ? 1 : 0,
          transform: show ? "translateY(0) scale(1)" : "translateY(12px) scale(0.985)",
          transition: "all 460ms cubic-bezier(0.16, 1, 0.3, 1)",
          transitionDelay: `${index * 110}ms`,

          "&:hover": {
            borderColor: option.color.base,

            backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.97)",
          },
        }}
      >
        {/* Checkbox top-right */}

        <Box
          sx={{
            width: { xs: 68, md: 200 },
            height: { xs: 68, md: 200 },
            borderRadius: "20px",
            mt: 0.25,
            mb: { xs: 1.75, md: 2 },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            //background: `linear-gradient(135deg, ${option.color[100]}, ${option.color[50]})`,
            //border: `2px solid ${option.color[200]}`,
          }}
        >
          {/*<Icon sx={{ fontSize: { xs: 32, md: 36 }, color: option.color.base }} />*/}
          <Box
            ref={containerRef}
            aria-label={option.title}
            sx={{
              width: { xs: 68, md: "100%" },
              height: { xs: 68, md: "100%" },
              marginTop: { xs: 1, md: -8 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 2, mb: 0.5, marginTop: -8 }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: 21, md: 24 }, color: "var(--fx-surface-text, #0F172A)", lineHeight: 1.08, mb: 0.5 }}>{option.title}</Typography>

          <Box
            sx={{
              borderRadius: "12px",
              backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.9)",
              padding: 1,

              placeItems: "center",
            }}
            onClick={(e) => {
              e.stopPropagation();
              console.log(option.mode);
            }}
          >
            <Checkbox
              checked={selected}
              onChange={(e) => {
                e.stopPropagation();
                onPick(option.mode);
              }}
              sx={{
                p: 0,
                "& .MuiSvgIcon-root": {
                  fontSize: 22,
                  color: selected ? option.color.base : "#999",
                },
              }}
            />
          </Box>
        </Box>

        <Typography
          sx={{
            color: option.color.base,
            fontSize: { xs: 12, md: 12.5 },
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            mb: { xs: 1.5, md: 1.75 },
          }}
        >
          {option.subtitle}
        </Typography>

        <Typography
          sx={{
            color: "var(--fx-surface-textsecondary, #64748B)",
            fontSize: { xs: 13, md: 14 },
            lineHeight: 1.45,
            px: { xs: 1, md: 1.75 },
            mb: { xs: 2, md: 2.25 },
            maxWidth: 380,
          }}
        >
          {option.description}
        </Typography>

        <Box
          sx={{
            mt: "auto",
            width: "100%",
            display: "flex",

            alignItems: "center",
            justifyContent: "space-between",
            p: 1.35,
            borderRadius: "16px",
            backgroundColor: option.color[50],
            border: `1px solid ${option.color[100]}`,
          }}
        >
          <Typography sx={{ fontWeight: 900, color: option.color.base, fontSize: { xs: 13, md: 14 }, margin: "auto" }}>{option.cta}</Typography>
          <ArrowForward sx={{ fontSize: { xs: 19, md: 20 }, color: option.color.base, opacity: 0.9 }} />
        </Box>
      </Box>
    </Grid>
  );
}

export function QuoteModeSelectorStep({
  title = "Create Service",
  subtitle = "Choose how you want to create it",
  onModeSelected,
}: {
  title?: string;
  subtitle?: string;
  onModeSelected?: (mode: QuoteModeEnum) => void;
}) {
  const dispatch = useAppDispatch();
  const selectedMode = useAppSelector((s) => s.jobCreateStepper.quoteMode) as QuoteModeEnum | null;

  const [showCards, setShowCards] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowCards(true), 150);
    return () => clearTimeout(t);
  }, []);

  const handlePick = (m: QuoteModeEnum) => {
    dispatch(setScopeOfWorkMode(m));
    onModeSelected?.(m);
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 4, overflow: "hidden", background: "transparent" }}>
      <Box sx={{ textAlign: "center", display: "grid", placeItems: "center", marginTop: 4 }}>
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: { xs: 30, md: 40 },
            background: "linear-gradient(135deg, #1a1a1a, #333)",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {/*<Typography sx={{ fontSize: { xs: 14, md: 16 }, color: "var(--fx-surface-textsecondary, #64748B)", fontWeight: 500, maxWidth: 520 }}>{subtitle}</Typography>*/}
      </Box>

      <Box p={{ xs: 2, md: 0, marginTop: -30 }}>
        <Box sx={{ width: "100%", maxWidth: 980, mx: "auto", pt: { xs: 1, md: -4 } }}>
          <Divider
            orientation="vertical"
            sx={{
              display: { xs: "none", md: "block" },
              position: "absolute",
              height: 200,
              left: "50%",
              top: 180,
              bottom: 0,
              transform: "translateX(-50%)",
              borderColor: colors.gray[200],
              opacity: 1,
            }}
          />
          <Grid container spacing={{ xs: 2.5, md: 3 }} sx={{ justifyContent: "center", alignItems: "stretch" }}>
            {modeOptions?.map((option, index) => (
              <ModeCard
                key={option.mode}
                option={option}
                index={index}
                show={showCards}
                selected={selectedMode === option.mode}
                onPick={handlePick}
              />
            ))}
          </Grid>
        </Box>
      </Box>
    </Paper>
  );
}
